import { ComponentPropsWithoutRef } from 'react';
import { useDragLayer } from 'react-dnd';

import { useChannels } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import {
  Box,
  Icon,
  TableBodyCell,
  TableCellExpander,
  Typography,
} from '@myn/mui';

import {
  BUILDER_ROW_DEPTH,
  ROW_HEIGHT,
} from '../../../constants/campaignBuilder.js';
import { dragItemTypes } from '../../../constants/myCampaigns.js';
import { DimensionsDragType } from '../../../features/EditModal/EditModal.constants.js';
import { useTableScale } from '../../../hooks/useTableScale.js';
import { Offset } from '../../../types/shared.js';
import PlatformImage from './BuilderTable/ColumnFields/NameField/PlatformImage.jsx';

const RowReorderDragItem = () => {
  const { t } = useTranslation('@myn/planning');

  const scale = useTableScale();
  const channelsMeta = useChannels();

  const { row, currentOffset, isDragging } = useDragLayer((monitor) => ({
    row: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (
    !isDragging ||
    !currentOffset ||
    Object.values(dragItemTypes).includes(row.type) ||
    Object.values(DimensionsDragType).includes(row.type)
  ) {
    return null;
  }

  return (
    <Wrapper sx={{ opacity: 0.8 }}>
      {/* @ts-ignore TODO: fix types */}
      <DragItem
        currentOffset={currentOffset}
        $scale={scale}
        $height={ROW_HEIGHT}
        maxWidth={row.cells[0]?.column.width}
        isExpanded={false}
      >
        <TableBodyCell
          sx={{
            width: '100%',
            height: '100%',
            '& > div': { gap: `${scale * 0.5}rem` },
          }}
        >
          <Icon component="Draggable" sx={{ transform: `scale(${scale})` }} />
          <TableCellExpander
            expanded={row.isExpanded}
            sx={{ border: 'none', svg: { transform: `scale(${scale})` } }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: `${scale * 8}px`,
            }}
          >
            {row.depth === BUILDER_ROW_DEPTH.CHANNEL ? (
              <Icon
                sx={{ transform: `scale(${scale})` }}
                component={channelsMeta[row.original.name]?.icon ?? 'Settings'}
              />
            ) : (
              <PlatformImage row={row} scale={scale} />
            )}
            <Typography sx={{ fontSize: `${scale}rem` }}>
              {t(row.original.label || row.original.name)}
            </Typography>
          </Box>
        </TableBodyCell>
      </DragItem>
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  cursor: grab;
`;

const DragItem = styled(
  ({
    currentOffset,
    maxWidth,
    isExpanded,
    $scale,
    $height,
    leftOffset,
    ...props
  }: ComponentPropsWithoutRef<'div'> & {
    currentOffset: Offset;
    maxWidth: number;
    isExpanded: boolean;
    $scale: number;
    $height: number;
    leftOffset: number;
  }) => (
    // @ts-ignore TODO: fix types
    <div style={{ top: currentOffset.y, left: currentOffset.x }} {...props} />
  ),
)`
  position: absolute;
  display: flex;
  align-items: center;
  width: ${({ $scale, maxWidth }) => maxWidth * 0.8 * $scale}px;
  overflow: hidden;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.25);
  height: ${({ $height, $scale }) => `${$height * $scale}px`};
`;

export default RowReorderDragItem;
